/**
 * WP-V2-TW — kotki uliczne ambient (głaskanie → serduszka + zaliczenie misji).
 * Placeholdery gdy brak cat_street_*.
 */
import Phaser from 'phaser';
import {
  INTERACT_BUS_EVENT,
  type QuestLayer,
} from '../../quests/index.js';
import { STREET_CAT_SPAWNS } from './hotspots.js';
import { depthForGroundY } from './townLayout.js';

/** Re-export spawów (źródło: hotspots — testowalne bez Phaser). */
export { STREET_CAT_SPAWNS };

/** Skala fallbacku wizualnego (×1.6 względem pierwotnego r=22 / emoji 28px). */
const FALLBACK_SCALE = 1.6;
const BASE_RADIUS = 22;
const BASE_EMOJI = 28;
const BASE_IMG = 56;
const HIT_RADIUS = Math.round(36 * FALLBACK_SCALE);

const MIAU_INTERVAL_SEC = 6;
const QUEST_ID = 'town_pet_cat';

type CatVis = {
  root: Phaser.GameObjects.Container;
  x: number;
  y: number;
  bobT: number;
  miauT: number;
};

export class StreetCats {
  private cats: CatVis[] = [];
  private arrow: Phaser.GameObjects.Container | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  spawn(): void {
    for (const c of STREET_CAT_SPAWNS) {
      const parts: Phaser.GameObjects.GameObject[] = [];
      const r = Math.round(BASE_RADIUS * FALLBACK_SCALE);
      parts.push(
        this.scene.add.ellipse(0, Math.round(18 * FALLBACK_SCALE), Math.round(36 * FALLBACK_SCALE), Math.round(12 * FALLBACK_SCALE), 0x000000, 0.12),
      );

      if (this.scene.textures.exists('cat_street_idle')) {
        const size = Math.round(BASE_IMG * FALLBACK_SCALE);
        parts.push(
          this.scene.add.image(0, 0, 'cat_street_idle').setDisplaySize(size, size),
        );
      } else {
        parts.push(
          this.scene.add
            .circle(0, 0, r, 0xffab91)
            .setStrokeStyle(4, 0xffffff),
        );
        parts.push(
          this.scene.add
            .text(0, -2, '🐱', {
              fontSize: `${Math.round(BASE_EMOJI * FALLBACK_SCALE)}px`,
            })
            .setOrigin(0.5),
        );
      }

      const hit = this.scene.add
        .circle(0, 0, HIT_RADIUS, 0xffffff, 0.001)
        .setInteractive({
          useHandCursor: true,
          hitArea: new Phaser.Geom.Circle(0, 0, HIT_RADIUS),
        });
      parts.push(hit);

      const diam = HIT_RADIUS * 2;
      const root = this.scene.add.container(c.x, c.y, parts);
      root.setSize(diam, diam);
      root.setDepth(depthForGroundY(c.y));
      root.setData('hotspot', 'street-cat');
      root.setName('street-cat');
      root.setScale(1);

      // delikatny pulse (łatwiej znaleźć kota)
      this.scene.tweens.add({
        targets: root,
        scaleX: 1.08,
        scaleY: 1.08,
        yoyo: true,
        repeat: -1,
        duration: 700,
        ease: 'Sine.easeInOut',
      });

      const vis: CatVis = {
        root,
        x: c.x,
        y: c.y,
        bobT: Math.random() * 4,
        miauT: Math.random() * MIAU_INTERVAL_SEC,
      };
      hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event?.stopPropagation?.();
        this.onPet(vis);
      });
      this.cats.push(vis);
    }

    this.arrow = this.buildArrow();
    this.arrow.setVisible(false);
  }

  private buildArrow(): Phaser.GameObjects.Container {
    const g = this.scene.add.graphics();
    g.fillStyle(0xff8a65, 0.95);
    g.lineStyle(3, 0xffffff, 0.9);
    // trójkąt wskazujący w prawo (obrót = kierunek)
    g.fillTriangle(-10, -14, -10, 14, 18, 0);
    g.strokeTriangle(-10, -14, -10, 14, 18, 0);
    const label = this.scene.add
      .text(0, -28, '🐱', { fontSize: '18px' })
      .setOrigin(0.5);
    const root = this.scene.add.container(0, 0, [g, label]);
    root.setDepth(200);
    root.setScrollFactor(1);
    return root;
  }

  private onPet(cat: CatVis): void {
    const heart = this.scene.add
      .text(0, -Math.round(36 * FALLBACK_SCALE), '💖', { fontSize: '32px' })
      .setOrigin(0.5);
    cat.root.add(heart);
    this.scene.tweens.add({
      targets: heart,
      y: -Math.round(70 * FALLBACK_SCALE),
      alpha: 0,
      duration: 700,
      onComplete: () => heart.destroy(),
    });
    this.scene.tweens.add({
      targets: cat.root,
      scaleX: 1.18,
      scaleY: 1.18,
      yoyo: true,
      duration: 160,
    });

    // zaliczenie misji „Pogłaszcz kotka na ulicy" (QuestLayer.onInteractBus)
    this.scene.game.events.emit(INTERACT_BUS_EVENT, {
      questId: QUEST_ID,
      sceneKey: 'Town',
      type: 'tap',
    });
  }

  /** Czy aktywna jest misja town_pet_cat (publiczne getState QuestLayer). */
  private isPetCatQuestActive(): boolean {
    const s = this.scene.game.scene.getScene('QuestLayer') as QuestLayer | null;
    if (!s || typeof s.getState !== 'function') return false;
    const active = s.getState().active;
    return active?.questId === QUEST_ID && active.status === 'active';
  }

  private showMiau(cat: CatVis): void {
    const bubble = this.scene.add
      .text(0, -Math.round(48 * FALLBACK_SCALE), 'Miau!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5);
    cat.root.add(bubble);
    this.scene.tweens.add({
      targets: bubble,
      y: -Math.round(72 * FALLBACK_SCALE),
      alpha: 0,
      duration: 1200,
      ease: 'Sine.easeOut',
      onComplete: () => bubble.destroy(),
    });
  }

  private nearestCat(from: { x: number; y: number }): CatVis | null {
    if (this.cats.length === 0) return null;
    let best: CatVis | null = null;
    let bestD = Infinity;
    for (const c of this.cats) {
      const dx = c.x - from.x;
      const dy = c.y - from.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }

  update(dtSec: number, playerPos: { x: number; y: number } | null = null): void {
    for (const c of this.cats) {
      c.bobT += dtSec * 2.4;
      c.root.y = c.y + Math.sin(c.bobT) * 2;

      c.miauT += dtSec;
      if (c.miauT >= MIAU_INTERVAL_SEC) {
        c.miauT -= MIAU_INTERVAL_SEC;
        this.showMiau(c);
      }
    }

    // strzałka do najbliższego kota gdy misja aktywna
    if (this.arrow) {
      const show = !!playerPos && this.isPetCatQuestActive();
      if (!show) {
        this.arrow.setVisible(false);
      } else {
        const nearest = this.nearestCat(playerPos!);
        if (!nearest) {
          this.arrow.setVisible(false);
        } else {
          const dx = nearest.x - playerPos!.x;
          const dy = nearest.y - playerPos!.y;
          const dist = Math.hypot(dx, dy);
          // ukryj gdy już blisko kota
          if (dist < 90) {
            this.arrow.setVisible(false);
          } else {
            const ang = Math.atan2(dy, dx);
            // strzałka tuż przed pieskiem w kierunku kota
            const ox = Math.cos(ang) * 48;
            const oy = Math.sin(ang) * 48;
            this.arrow.setPosition(playerPos!.x + ox, playerPos!.y + oy - 20);
            this.arrow.setRotation(ang);
            this.arrow.setVisible(true);
          }
        }
      }
    }
  }

  destroy(): void {
    for (const c of this.cats) c.root.destroy(true);
    this.cats = [];
    this.arrow?.destroy(true);
    this.arrow = null;
  }
}
